import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProjectStatus, CaseAward } from '@lnr-v2/types';
import { FormField } from '../../molecules/FormField';
import { Button } from '../../atoms/Button';
import { ImageUploader } from '../../molecules/ImageUploader';
import { TermSelector } from '../../molecules/TermSelector';
import { TagSelector } from '../../molecules/TagSelector';
import { ClientSelector } from '../../molecules/ClientSelector';
import styles from './SimpleCaseForm.module.css';

const AWARD_NAME_OPTIONS = [
  'awwwards',
  'CSSDesignAwards',
  'FWA',
  'Content squarer',
  'Webflow',
  'codrops',
  'Association nationale des communicants',
  'M',
  'FrenchDesign',
];

const STATUS_OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'DRAFT', label: 'Brouillon' },
  { value: 'IN_PROGRESS', label: 'En cours' },
  { value: 'COMPLETED', label: 'Publié' },
  { value: 'ARCHIVED', label: 'Archivé' },
];

export interface SimpleCaseFormProps {
  initialClientId?: string;
  initialData?: Partial<SimpleCaseFormData>;
  onSubmit: (data: SimpleCaseFormData) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface SimpleCaseFormData {
  title: string;
  clientId: string;
  slogan?: string;
  shortDescription?: string;
  descriptionEN?: string;
  descriptionFR?: string;
  featuredImage?: string;
  mobileImage?: string;
  termIds: string[];
  tagIds: string[];
  isFeatured: boolean;
  sortOrder: number;
  status: ProjectStatus;
  awards: CaseAward[];
  slug?: string; // Added for translation link
}

export const SimpleCaseForm: React.FC<SimpleCaseFormProps> = ({
  initialClientId,
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  // Detect edit mode
  const isEditMode = !!initialData;

  // Form state - optimized with useMemo for initial values
  const initialFormData = useMemo<SimpleCaseFormData>(
    () => ({
      title: initialData?.title || '',
      clientId: initialData?.clientId || initialClientId || '',
      slogan: initialData?.slogan || '',
      shortDescription: initialData?.shortDescription || '',
      descriptionEN: initialData?.descriptionEN || '',
      descriptionFR: initialData?.descriptionFR || '',
      featuredImage: initialData?.featuredImage || undefined,
      mobileImage: initialData?.mobileImage || undefined,
      termIds: initialData?.termIds || [],
      tagIds: initialData?.tagIds || [],
      isFeatured: initialData?.isFeatured ?? false,
      sortOrder: initialData?.sortOrder ?? 1,
      status: initialData?.status || 'DRAFT',
      awards: initialData?.awards || [],
    }),
    [initialClientId, initialData]
  );

  const [formData, setFormData] = useState<SimpleCaseFormData>(initialFormData);

  const handleChange = (
    field: keyof SimpleCaseFormData,
    value: string | number | boolean | string[] | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageChange = (url: string | null) => {
    handleChange('featuredImage', url || undefined);
  };

  const handleMobileImageChange = (url: string | null) => {
    handleChange('mobileImage', url || undefined);
  };

  const handleAddAward = () => {
    setFormData((prev) => ({
      ...prev,
      awards: [...prev.awards, { key: '' }],
    }));
  };

  const handleRemoveAward = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      awards: prev.awards.filter((_, i) => i !== index),
    }));
  };

  const handleAwardChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      awards: prev.awards.map((award, i) =>
        i === index ? ({ ...award, key: value } as CaseAward) : award
      ),
    }));

    if (errors.awards) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.awards;
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Case title is required';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (formData.awards.some((award) => !award.key?.trim())) {
      newErrors.awards = 'Each award needs a key (example: fwa, awwwards)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.simpleCaseForm}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>
          {isEditMode ? 'Edit Case' : 'Create New Case'}
        </h2>
        <p className={styles.formDescription}>
          {isEditMode
            ? 'Update the essential information for this case.'
            : 'Add the essential information to create a case. You can add more details later.'}
        </p>
      </div>

      <div className={styles.formBody}>
        {/* Essential Information */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Essential Information</h3>

          <FormField
            label="Case Title"
            name="title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter case title"
            maxLength={200}
          />

          <ClientSelector
            selectedClientId={formData.clientId}
            onChange={(clientId) => handleChange('clientId', clientId)}
            error={errors.clientId}
            required
            placeholder="Select a client *"
            disabled={loading || uploading}
          />

          <FormField
            label="Slogan"
            name="slogan"
            value={formData.slogan || ''}
            onChange={(e) => handleChange('slogan', e.target.value)}
            placeholder="e.g., Redefining digital experiences"
            maxLength={255}
            helpText="Brand slogan or tagline for this case"
          />

          <FormField
            label="Quick Description"
            name="shortDescription"
            type="textarea"
            value={formData.shortDescription || ''}
            onChange={(e) => handleChange('shortDescription', e.target.value)}
            placeholder="A brief elevator pitch or tagline (max 200 characters)"
            maxLength={200}
            rows={3}
            helpText="This appears in cards and previews"
          />
        </div>

        {/* Bilingual Description */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Project Description</h3>
          <p className={styles.helpText}>
            Full description displayed in the Case Info block. Add both
            languages for bilingual support.
          </p>

          <div className={styles.bilingualFields}>
            <FormField
              label="🇬🇧 Description (English)"
              name="descriptionEN"
              type="textarea"
              value={formData.descriptionEN || ''}
              onChange={(e) => handleChange('descriptionEN', e.target.value)}
              placeholder="Full project description in English..."
              rows={4}
            />

            <FormField
              label="🇫🇷 Description (Français)"
              name="descriptionFR"
              type="textarea"
              value={formData.descriptionFR || ''}
              onChange={(e) => handleChange('descriptionFR', e.target.value)}
              placeholder="Description complète du projet en français..."
              rows={4}
            />
          </div>
        </div>

        {/* Media */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Cover Image</h3>

          <ImageUploader
            value={formData.featuredImage}
            onChange={handleImageChange}
            onUploadStart={() => setUploading(true)}
            onUploadComplete={() => setUploading(false)}
            onUploadError={(error) => {
              setUploading(false);
              setErrors((prev) => ({ ...prev, featuredImage: error }));
            }}
            folder="cases"
            maxSize={10}
            disabled={loading || uploading}
            label="Featured Image (optional)"
            helpText="PNG, JPG, GIF up to 10MB"
          />

          <div style={{ height: '16px' }} />

          <ImageUploader
            value={formData.mobileImage}
            onChange={handleMobileImageChange}
            onUploadStart={() => setUploading(true)}
            onUploadComplete={() => setUploading(false)}
            onUploadError={(error) => {
              setUploading(false);
              setErrors((prev) => ({ ...prev, mobileImage: error }));
            }}
            folder="cases"
            maxSize={10}
            disabled={loading || uploading}
            label="Mobile Image (optional)"
            helpText="Displayed in Cases list on mobile screens (PNG, JPG, GIF up to 10MB)"
          />
        </div>

        {/* Categorization */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Categorization</h3>
          <p className={styles.helpText}>
            Select terms to categorize this case. You can search and select
            multiple terms across different taxonomies.
          </p>
          <TermSelector
            selectedTermIds={formData.termIds}
            onChange={(termIds) => handleChange('termIds', termIds)}
            lang="en"
            placeholder="Search terms (Services, Industries, Technologies)..."
            groupByTaxonomy
          />

          <h4
            className={styles.subSectionTitle}
            style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}
          >
            Tags (Highlights)
          </h4>
          <p className={styles.helpText}>
            Select tags to highlight specific features or keywords.
          </p>
          <TagSelector
            selectedTags={formData.tagIds}
            onChange={(tagIds) => handleChange('tagIds', tagIds)}
            placeholder="Search tags..."
          />
        </div>

        {/* Awards */}
        <div className={styles.section}>
          <div className={styles.awardsHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Awards / Prix</h3>
              <p className={styles.helpText}>
                Select award names for this project.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddAward}
              disabled={loading || uploading}
            >
              Add Award
            </Button>
          </div>

          {errors.awards && <p className={styles.errorText}>{errors.awards}</p>}

          {formData.awards.length === 0 ? (
            <p className={styles.emptyAwards}>No awards added yet.</p>
          ) : (
            <div className={styles.awardsList}>
              {formData.awards.map((award, index) => (
                <div key={`award-${index}`} className={styles.awardCard}>
                  <div className={styles.awardCardHeader}>
                    <h4 className={styles.awardCardTitle}>
                      Award #{index + 1}
                    </h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAward(index)}
                      disabled={loading || uploading}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className={styles.awardGrid}>
                    <FormField
                      label="Award Name"
                      name={`award-key-${index}`}
                      type="select"
                      value={award.key || ''}
                      onChange={(e) => handleAwardChange(index, e.target.value)}
                      options={[
                        { value: '', label: 'Select an award' },
                        ...AWARD_NAME_OPTIONS.map((name) => ({
                          value: name,
                          label: name,
                        })),
                      ]}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Display Settings */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Publication & Display</h3>

          <FormField
            label="Statut"
            name="status"
            type="select"
            value={formData.status}
            onChange={(e) =>
              handleChange('status', e.target.value as ProjectStatus)
            }
            options={STATUS_OPTIONS}
            required
            helpText="Seuls les cases 'Publié' apparaissent sur le site"
          />

          <div className={styles.checkboxField}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.isFeatured}
                onChange={(e) => handleChange('isFeatured', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Featured Case</span>
            </label>
            <p className={styles.helpText}>
              Featured cases appear prominently on the homepage
            </p>
          </div>

          <FormField
            label="Display Order"
            name="sortOrder"
            type="number"
            value={formData.sortOrder}
            onChange={(e) =>
              handleChange('sortOrder', parseInt(e.target.value, 10) || 1)
            }
            min={1}
            helpText="Lower numbers appear first (minimum: 1)"
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className={styles.formActions}>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>

        {isEditMode && initialData?.slug && (
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={() =>
              navigate(`/admin/translations?filter=cases.${initialData.slug}`)
            }
            disabled={loading || uploading}
          >
            Manage Translations
          </Button>
        )}

        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={loading || uploading}
        >
          {loading
            ? isEditMode
              ? 'Updating...'
              : 'Creating...'
            : uploading
              ? 'Uploading...'
              : isEditMode
                ? 'Update Case'
                : 'Create Case'}
        </Button>
      </div>

      {!isEditMode && (
        <p className={styles.formNote}>
          Note: Additional details like description, challenge, solution, and
          testimonials can be added after creating the case.
        </p>
      )}
    </form>
  );
};
